const CART_DRAWER_RECOMMENDATIONS_ID = 'CartDrawer-Recommendations'

/**
 * Patch the cart drawer in place against freshly rendered markup.
 *
 * Rewriting innerHTML re-creates every node, which reloads line item images,
 * drops focus and remounts the recommendations carousel. Morphing only touches
 * what actually differs, so untouched parts of the drawer never repaint.
 */
function replaceCartDrawerHtml(elementToReplace, newInnerHTML) {
  const staging = document.createElement('div')
  staging.innerHTML = newInnerHTML

  try {
    morphChildren(elementToReplace, staging)
  } catch (error) {
    console.error('[cart-drawer] morph failed, rebuilding drawer', error)
    elementToReplace.innerHTML = newInnerHTML
  }
}

function morphNode(current, next) {
  if (
    current.nodeType !== next.nodeType ||
    current.nodeName !== next.nodeName
  ) {
    current.replaceWith(next)
    return
  }

  if (current.nodeType !== Node.ELEMENT_NODE) {
    if (current.nodeValue !== next.nodeValue) {
      current.nodeValue = next.nodeValue
    }
    return
  }

  // The server only renders the recommendations shell, never the live slides
  if (
    current.getAttribute('sub-section-id') === CART_DRAWER_RECOMMENDATIONS_ID
  ) {
    syncCartDrawerRecommendations(current, next)
    return
  }

  morphAttributes(current, next)

  // Widgets injected by apps have no server markup; morphing would strip them
  if (isClientOwned(current)) return

  morphChildren(current, next)
}

/**
 * Elements whose contents are populated client side (the Wrapped gift-wrap
 * app). Their server markup is an empty shell, so their children are ours to
 * leave alone.
 */
function isClientOwned(element) {
  return element.nodeName.startsWith('WRAPPED-')
}

function morphAttributes(current, next) {
  Array.from(next.attributes).forEach((attribute) => {
    if (current.getAttribute(attribute.name) !== attribute.value) {
      current.setAttribute(attribute.name, attribute.value)
    }
  })

  Array.from(current.attributes).forEach((attribute) => {
    if (!next.hasAttribute(attribute.name)) {
      current.removeAttribute(attribute.name)
    }
  })

  // Attributes don't drive an input's displayed value once it has been rendered
  if (current instanceof HTMLInputElement && next.hasAttribute('value')) {
    const nextValue = next.getAttribute('value')
    if (current.value !== nextValue) {
      current.value = nextValue
    }
  }
}

/**
 * Identity for matching nodes across renders. Position alone isn't reliable —
 * the live drawer carries nodes the server never rendered.
 *
 * `data-morph-key` outranks `id` because cart rows are numbered by their
 * position in the cart (`CartItem-2`), which shifts as soon as any earlier
 * line is removed. Matching on that would hand a row to a different product
 * while keeping the client-owned children the previous product left behind.
 */
function nodeKey(node) {
  if (node.nodeType !== Node.ELEMENT_NODE) return null
  return (
    node.getAttribute('data-morph-key') ||
    node.getAttribute('sub-section-id') ||
    node.id ||
    null
  )
}

function isMorphable(current, next) {
  return (
    current.nodeType === next.nodeType &&
    current.nodeName === next.nodeName &&
    nodeKey(current) === nodeKey(next)
  )
}

/**
 * A key only identifies a node when it appears once. Repeated ids do occur
 * (every `bundle-line-item` renders as `BundleLine`), so drop those keys and
 * let the siblings fall back to positional matching.
 */
function collectKeyedChildren(parent) {
  const keyed = new Map()
  const duplicates = new Set()

  Array.from(parent.childNodes).forEach((child) => {
    const key = nodeKey(child)
    if (!key) return
    if (keyed.has(key)) {
      duplicates.add(key)
      return
    }
    keyed.set(key, child)
  })

  duplicates.forEach((key) => keyed.delete(key))
  return keyed
}

function morphChildren(parent, next) {
  const nextChildren = Array.from(next.childNodes)
  const keyed = collectKeyedChildren(parent)

  let cursor = parent.firstChild

  nextChildren.forEach((nextChild) => {
    const key = nodeKey(nextChild)
    const keyedMatch = key ? keyed.get(key) : null
    let match = null

    if (keyedMatch && keyedMatch.nodeName === nextChild.nodeName) {
      match = keyedMatch
    } else {
      let candidate = cursor
      while (candidate) {
        if (isMorphable(candidate, nextChild)) {
          match = candidate
          break
        }
        candidate = candidate.nextSibling
      }
    }

    if (!match) {
      parent.insertBefore(nextChild, cursor)
      return
    }

    const matchedKey = nodeKey(match)
    if (matchedKey) keyed.delete(matchedKey)

    if (match !== cursor) {
      parent.insertBefore(match, cursor)
    }
    cursor = match.nextSibling
    morphNode(match, nextChild)
  })

  while (cursor) {
    const stale = cursor
    cursor = cursor.nextSibling
    stale.remove()
  }
}

/**
 * Recommendations can't be compared by markup — the server only ever returns
 * the loading shell, never the rendered slides. Compare the input key (cart
 * SKUs) instead and refresh the mounted carousel in place when it changes.
 */
function syncCartDrawerRecommendations(region, next) {
  const nextKey = next.getAttribute('data-rec-key')
  const unchanged = nextKey === region.getAttribute('data-rec-key')
  const recommendations = region.querySelector('.searchspring-recommendations')

  if (recommendations && unchanged) {
    return
  }

  if (
    recommendations?.classList.contains('is-loaded') &&
    typeof recommendations.refresh === 'function'
  ) {
    // Record the key only once the carousel is showing slides for it. Writing
    // it up front would make a later update to the same SKUs look like a
    // no-op, stranding the drawer on recommendations for an older cart.
    Promise.resolve(recommendations.refresh(nextKey))
      .then((refreshed) => {
        if (refreshed) region.setAttribute('data-rec-key', nextKey)
      })
      .catch((error) => {
        console.error('[cart-drawer] recommendations refresh failed', error)
      })
    return
  }

  region.replaceWith(next)
  window.mountSearchspringRecommendations?.(next)
}

window.replaceCartDrawerHtml = replaceCartDrawerHtml

class CartDrawer extends HTMLElement {
  constructor() {
    super()
    this.close = this.close.bind(this)
    this.setHeaderCartIconAccessibility()
  }
  connectedCallback() {
    this.handleOverlayClick = this.handleOverlayClick.bind(this)
    this.handleKeyup = (evt) => evt.code === 'Escape' && this.close()
    this.addEventListener('click', this.handleOverlayClick)
    this.addEventListener('keyup', this.handleKeyup)
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleOverlayClick)
    this.removeEventListener('keyup', this.handleKeyup)
  }

  handleOverlayClick(evt) {
    if (evt.target.id === 'CartDrawer-Overlay') {
      this.close()
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble')
    cartLink.setAttribute('role', 'button')
    cartLink.setAttribute('aria-haspopup', 'dialog')
    cartLink.addEventListener('click', (event) => {
      event.preventDefault()
      this.open(cartLink)
    })
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault()
        this.open(cartLink)
      }
    })
  }

  open(triggeredBy, showCartItems = false) {
    if (triggeredBy) this.setActiveElement(triggeredBy)
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary')
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
      this.setSummaryAccessibility(cartDrawerNote)

    this.setAttribute('data-open', '')
    let containerToTrapFocusOn = null
    const cartDrawerWrapper = document.querySelector('cart-drawer')
    const cartFooter = document.getElementById('main-cart-footer')
    if (showCartItems) {
      containerToTrapFocusOn = document.getElementById('CartDrawer__Dialog')
      cartDrawerWrapper.removeAttribute('data-empty')
    } else {
      containerToTrapFocusOn =
        this.getAttribute('data-empty') === 'true'
          ? this.querySelector('[data-cart-inner-empty]')
          : document.getElementById('CartDrawer__Dialog')
    }
    if (containerToTrapFocusOn) {
      const focusElement =
        containerToTrapFocusOn.querySelector('[data-cart-inner]') ||
        containerToTrapFocusOn.querySelector('[data-cart-close]')
      trapFocus(containerToTrapFocusOn, focusElement)
    }

    document.body.classList.add('overflow-hidden')
  }

  close() {
    this.removeAttribute('data-open')
    removeTrapFocus(this.activeElement)
    document.body.classList.remove('overflow-hidden')
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button')
    cartDrawerNote.setAttribute('aria-expanded', 'false')

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      )
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      )
    })

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape)
  }

  renderContents(parsedState) {
    this.productId = parsedState.id

    if (parsedState.sections) {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id)
        const html = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        )
        if (
          section.id === 'cart-drawer' ||
          section.selector === '#CartDrawer__Dialog'
        ) {
          replaceCartDrawerHtml(sectionElement, html)
        } else {
          sectionElement.innerHTML = html
        }
      })
      // Keep host in sync with re-rendered markup: group-data-[empty=true]:hidden
      // targets this element; stale data-empty="true" hides the flyout until JS clears it.
      const drawerItems = this.querySelector('cart-drawer-items')
      if (drawerItems?.hasAttribute('data-empty')) {
        this.setAttribute('data-empty', drawerItems.getAttribute('data-empty'))
      } else if ('item_count' in parsedState) {
        this.setAttribute(
          'data-empty',
          parsedState.item_count === 0 ? 'true' : 'false'
        )
      }
    }

    setTimeout(() => this.open())
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer__Dialog',
      },
      {
        id: 'cart-icon-bubble',
      },
    ]
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)
  }

  setActiveElement(element) {
    this.activeElement = element
  }
}

customElements.define('cart-drawer', CartDrawer)

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const cartPageItems = document.querySelector('cart-items')

    const sections = [
      {
        id: 'CartDrawer__Dialog',
        section: 'cart-drawer',
        selector: '[data-cart-inner]',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ]

    if (cartPageItems) {
      cartPageItems.getSectionsToRender().forEach((section) => {
        if (!sections.find((item) => item.section === section.section)) {
          sections.push(section)
        }
      })
    }

    return sections
  }
}

customElements.define('cart-drawer-items', CartDrawerItems)
